import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Edit, Trash2, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { uploadToCloudinary } from '../../utils/cloudinary';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminProducts = () => {
  const { getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category_id: '',
    reference: '',
    description: '',
    price: '',
    promo_price: '',
    omit_price: false,
    is_featured: false,
    featured_order: '',
    is_active: true,
    images: []
  });

  const [searchTerm, setSearchTerm] = useState('');

  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/products?limit=2000`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/categories`, { headers: getAuthHeader() })
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: fieldValue
      };

      if (name === 'name' && !editingProduct) {
        updated.slug = generateSlug(value);
      }

      if (name === 'omit_price' && checked) {
        updated.price = '';
        updated.promo_price = '';
      }

      return updated;
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    const newImages = [...formData.images];

    for (const file of files) {
      const result = await uploadToCloudinary(file, 'central-ferragens/products', getAuthHeader());
      if (result.success) {
        newImages.push({
          image_url: result.url,
          sort_order: newImages.length
        });
      } else {
        toast.error(`Erro ao fazer upload de ${file.name}`);
      }
    }

    setFormData(prev => ({ ...prev, images: newImages }));
    setUploadingImages(false);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const moveImage = (index, direction) => {
    const newImages = [...formData.images];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newImages.length) return;

    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    newImages.forEach((img, i) => img.sort_order = i);

    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        omit_price: !!formData.omit_price,
        price: formData.omit_price ? null : parseFloat(formData.price),
        promo_price:
          formData.omit_price
            ? null
            : (formData.promo_price ? parseFloat(formData.promo_price) : null),
        featured_order: formData.featured_order ? parseInt(formData.featured_order) : null
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Produto atualizado com sucesso!');
      } else {
        await axios.post(`${API_URL}/products`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Produto criado com sucesso!');
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      category_id: product.category_id,
      reference: product.reference || '',
      description: product.description || '',
      price: product.price != null ? product.price.toString() : '',
      promo_price: product.promo_price ? product.promo_price.toString() : '',
      omit_price: !!product.omit_price,
      is_featured: product.is_featured,
      featured_order: product.featured_order ? product.featured_order.toString() : '',
      is_active: product.is_active,
      images: product.images || []
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja deletar este produto?')) return;

    try {
      await axios.delete(`${API_URL}/products/${productId}`, {
        headers: getAuthHeader()
      });
      toast.success('Produto deletado com sucesso!');
      loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erro ao deletar produto');
    }
  };

  const handleToggleStatus = async (product) => {
    try {
      setUpdatingStatusId(product.id);

      await axios.put(
        `${API_URL}/products/${product.id}`,
        {
          is_active: !product.is_active
        },
        {
          headers: getAuthHeader()
        }
      );

      toast.success(
        `Produto ${!product.is_active ? 'ativado' : 'inativado'} com sucesso!`
      );

      setProducts((prevProducts) =>
        prevProducts.map((item) =>
          item.id === product.id
            ? { ...item, is_active: !item.is_active }
            : item
        )
      );
    } catch (error) {
      console.error('Error updating product status:', error);
      toast.error('Erro ao atualizar status do produto');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      slug: '',
      category_id: '',
      reference: '',
      description: '',
      price: '',
      promo_price: '',
      omit_price: false,
      is_featured: false,
      featured_order: '',
      is_active: true,
      images: []
    });
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'N/A';
  };

  const filteredProducts = products.filter((product) => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return true;

    const productName = product.name?.toLowerCase() || '';
    const productReference = product.reference?.toLowerCase() || '';

    return (
      productName.includes(term) ||
      productReference.includes(term)
    );
  });

  return (
    <AdminLayout>
      <div>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#273e82', fontFamily: 'Manrope' }}>
              Produtos
            </h1>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:items-center">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome ou referência"
              className="input-field min-w-[320px]"
              data-testid="products-search-input"
            />

            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="primary-button flex items-center justify-center gap-2 w-[200px] h-[46px] shrink-0"
              data-testid="add-product-button"
            >
              <Plus size={20} />
              Novo Produto
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full" data-testid="products-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Imagem</th>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Nome</th>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Categoria</th>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Preço</th>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Status</th>
                <th className="text-right p-4 font-semibold" style={{ color: '#273e82' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-4">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={product.images[0].image_url}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-200 rounded-lg"></div>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="font-semibold">{product.name}</p>
                      {product.reference && (
                        <p className="text-sm text-slate-500 mt-1">Ref: {product.reference}</p>
                      )}
                      {product.is_featured && (
                        <span
                          className="inline-block px-2 py-1 rounded text-xs font-bold mt-1"
                          style={{ backgroundColor: '#fef000', color: '#273e82' }}
                        >
                          DESTAQUE
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-slate-600">{getCategoryName(product.category_id)}</td>
                    <td className="p-4">
                      {product.omit_price ? (
                        <p className="font-semibold text-slate-600">
                          Adicione ao carrinho e consulte o preço
                        </p>
                      ) : product.promo_price ? (
                        <div>
                          <p className="line-through text-slate-400 text-sm">R$ {product.price.toFixed(2)}</p>
                          <p className="font-bold" style={{ color: '#273e82' }}>R$ {product.promo_price.toFixed(2)}</p>
                        </div>
                      ) : (
                        <p className="font-bold" style={{ color: '#273e82' }}>R$ {product.price.toFixed(2)}</p>
                      )}
                    </td>
                    <td className="p-4">
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(product)}
                        disabled={updatingStatusId === product.id}
                        className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold transition-all ${product.is_active
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                          } ${updatingStatusId === product.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                        data-testid={`toggle-status-${product.id}`}
                      >
                        {updatingStatusId === product.id
                          ? 'SALVANDO...'
                          : product.is_active
                            ? 'ATIVO'
                            : 'INATIVO'}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit size={18} style={{ color: '#273e82' }} />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 size={18} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-slate-500">
                    Nenhum produto encontrado para essa busca.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div
            className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="product-modal"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{ color: '#273e82', fontFamily: 'Manrope' }}>
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                    Nome do Produto *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="product-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                    Slug *
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="product-slug-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                    Categoria *
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="product-category-select"
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                    Referência
                  </label>
                  <input
                    type="text"
                    name="reference"
                    value={formData.reference}
                    onChange={handleInputChange}
                    className="input-field"
                    data-testid="product-reference-input"
                  />
                </div>
                <div className="flex items-center gap-2 mt-8">
                  <input
                    type="checkbox"
                    name="omit_price"
                    checked={formData.omit_price}
                    onChange={handleInputChange}
                    className="w-5 h-5"
                    data-testid="product-omit-price-checkbox"
                  />
                  <span className="font-semibold" style={{ color: '#273e82' }}>
                    Omitir preço (mostrar “Adicione ao carrinho e consulte o preço”)
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                    Preço (R$) {!formData.omit_price && '*'}
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    step="0.01"
                    required={!formData.omit_price}
                    disabled={formData.omit_price}
                    className={`input-field ${formData.omit_price ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
                    data-testid="product-price-input"
                  />
                  {formData.omit_price && (
                    <p className="text-xs text-slate-500 mt-2">
                      O preço será ocultado no site.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                    Preço Promocional (R$)
                  </label>
                  <input
                    type="number"
                    name="promo_price"
                    value={formData.promo_price}
                    onChange={handleInputChange}
                    step="0.01"
                    disabled={formData.omit_price}
                    className={`input-field ${formData.omit_price ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
                    data-testid="product-promo-price-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                    Ordem de Destaque
                  </label>
                  <input
                    type="number"
                    name="featured_order"
                    value={formData.featured_order}
                    onChange={handleInputChange}
                    className="input-field"
                    data-testid="product-featured-order-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="input-field resize-none"
                  data-testid="product-description-input"
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                    className="w-5 h-5"
                    data-testid="product-featured-checkbox"
                  />
                  <span className="font-semibold" style={{ color: '#273e82' }}>Produto em Destaque</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-5 h-5"
                    data-testid="product-active-checkbox"
                  />
                  <span className="font-semibold" style={{ color: '#273e82' }}>Produto Ativo</span>
                </label>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                  Imagens do Produto
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploadingImages}
                  />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <Upload size={40} className="mx-auto mb-4 text-slate-400" />
                    <p className="text-slate-600 mb-2">
                      {uploadingImages ? 'Fazendo upload...' : 'Clique para adicionar imagens'}
                    </p>
                    <p className="text-sm text-slate-400">
                      PNG, JPG até 10MB
                    </p>
                  </label>
                </div>

                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.image_url}
                          alt={`Product ${index + 1}`}
                          className="w-full aspect-square object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => moveImage(index, 'up')}
                              className="bg-white text-slate-700 p-2 rounded-lg"
                            >
                              ←
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="bg-red-500 text-white p-2 rounded-lg"
                          >
                            <Trash2 size={18} />
                          </button>
                          {index < formData.images.length - 1 && (
                            <button
                              type="button"
                              onClick={() => moveImage(index, 'down')}
                              className="bg-white text-slate-700 p-2 rounded-lg"
                            >
                              →
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 rounded-lg font-bold hover:bg-slate-50 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 primary-button disabled:opacity-50"
                  data-testid="submit-product-button"
                >
                  {loading ? 'Salvando...' : editingProduct ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminProducts;
